import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {BaseWrapperComponent} from '@twpub/shared/components/base';
import {AttachmentRender} from '@twpub/core/models/wrappers/attachment-render';
import {ComponentCategory} from '@twpub/core/enums';
import {AttachmentRenderDirective} from '@twpub/shared/components/wrappers';
import {VirtualFile} from '@twpub/core/models';
import {VirtualFileService} from '@twpub/core/services/virtual-file.service';
import {hasPropChanges} from '@twpub/core/utils';
import {forkJoin, of} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {AttachmentRenderDirective as AttachmentRenderDirective_1} from './attachment-render.directive';

@Component({
  selector: 'pub-attachment-render-wrapper',
  template: '<ng-template pubAttachmentRender></ng-template>',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [AttachmentRenderDirective_1]
})
export class AttachmentRenderWrapperComponent extends BaseWrapperComponent<AttachmentRender> implements OnChanges, OnDestroy {
  override compCategory = ComponentCategory.AttachmentRender;

  @Input() fileId!: number;
  vfile?: VirtualFile;
  thumbUrl?: string;
  contentUrl?: string;
  @ViewChild(forwardRef(() => AttachmentRenderDirective), {static: true}) pubAttachmentRender!: AttachmentRenderDirective

  // Blob URLs created for this attachment; revoked when the file changes or the component is destroyed.
  private objectUrls: string[] = [];

  constructor(injector: Injector, private fileService: VirtualFileService, private changeRef: ChangeDetectorRef) {
    super(injector);
  }

  protected override handleSessionChanges() {
    this.logger.debug('AttachmentRenderWrapperComponent.handleSessionChanges:', {fileId: this.fileId})
    this.loadFile()
  }

  private loadFile() {
    if (!this.fileId) {
      return;
    }
    this.revokeObjectUrls();
    // Fetch through the authenticated HttpClient; <img>/<video>/<object> src/data can't carry the auth token.
    forkJoin({
      vfile: this.fileService.getVirtualFile(this.fileId),
      content: this.fileService.getContentBlob(this.fileId).pipe(catchError(() => of<Blob | undefined>(undefined))),
      thumb: this.fileService.getContentBlob(this.fileId, true).pipe(catchError(() => of<Blob | undefined>(undefined)))
    }).subscribe({
      next: ({vfile, content, thumb}) => {
        this.vfile = vfile;
        this.contentUrl = this.toObjectUrl(content);
        this.thumbUrl = this.toObjectUrl(thumb) ?? this.contentUrl;
        this.update();
      },
      error: (error) => this.logger.error('AttachmentRenderWrapperComponent.loadFile failed', {
        fileId: this.fileId,
        error
      })
    })
  }

  private toObjectUrl(blob?: Blob): string | undefined {
    if (!blob || blob.size === 0) {
      return undefined;
    }
    const url = URL.createObjectURL(blob);
    this.objectUrls.push(url);
    return url;
  }

  private revokeObjectUrls() {
    this.objectUrls.forEach(url => URL.revokeObjectURL(url));
    this.objectUrls = [];
  }

  override ngOnChanges(changes: SimpleChanges) {
    if (hasPropChanges(changes, ['fileId'])) {
      this.loadFile();
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.revokeObjectUrls();
  }

  getViewContainerRef() {
    return this.pubAttachmentRender.viewContainerRef;
  }

  override update() {
    super.update((comp) => {
      comp.vfile = this.vfile;
      comp.thumbUrl = this.thumbUrl;
      comp.contentUrl = this.contentUrl;
      // Need explicit call to detect changes since this is used within wrapper component and value is not updated from wrapper
      this.changeRef.detectChanges();
      this.logger.debug('AttachmentRenderWrapperComponent.updated child:', {file: this.vfile?.id})
    })
  }
}
